import { useState, useEffect, useMemo, useRef } from 'react';
import api from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Textarea } from '@/components/ui/textarea';
import {
  Plus, Save, Trash2, AlertTriangle, Ruler,
  ChevronDown, ChevronRight, Paperclip, Download, Upload,
} from 'lucide-react';
import { toast } from 'sonner';
import { toastApiError } from '@/lib/errors';
import StatusBadge from '@/components/StatusBadge';
import EmptyState from '@/components/EmptyState';
import { Checkbox } from '@/components/ui/checkbox';
import useBulkSelect, { bulkDelete } from '@/hooks/useBulkSelect';
import BulkDeleteBar from '@/components/BulkDeleteBar';
import useLaboratories from '@/hooks/useLaboratories';

const EMPTY_ETALON_DRAFT = {
  code: '', name: '', serialNumber: '',
  laboratory: '', unitId: '',
  rangeFrom: null, rangeTo: null,
  notes: '', calibrations: [],
};

const ETALON_REQUIRED = [
  { field: 'code', label: 'Označení' },
  { field: 'name', label: 'Název' },
  { field: 'serialNumber', label: 'Výrobní číslo' },
  { field: 'laboratory', label: 'Laboratoř' },
  { field: 'unitId', label: 'Jednotka' },
];

// Backend base URL pro download linky příloh (musí být absolute, ne /api).
const BACKEND_BASE = process.env.REACT_APP_BACKEND_URL || '';

export default function EtalonPage() {
  const [etalons, setEtalons] = useState([]);
  const [units, setUnits] = useState([]);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({});
  const [search, setSearch] = useState('');
  const [calForm, setCalForm] = useState({ certificateNumber: '', issueDate: '', validUntil: '', status: 'active' });
  // Per-cert collapse state pro sub-sekci korekcí + přílohy
  const [expandedCerts, setExpandedCerts] = useState(() => new Set());
  const fileInputRef = useRef(null);
  const [uploadingFor, setUploadingFor] = useState(null); // cert index nebo null

  // Laboratoře (= veličiny) — single source of truth na BE
  // (`GET /api/laboratories`). `name` se používá jako jednotný label napříč
  // stránkami. Hook je async: `[]` na prvním renderu, pak se naplní (cache).
  const labs = useLaboratories();
  const LABORATORIES = labs.map((l) => ({ code: l.code, label: l.name }));
  const LAB_LABEL = Object.fromEntries(labs.map((l) => [l.code, l.name]));

  const load = () => api.get('/etalons').then(r => setEtalons(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get('/units').then(r => setUnits(r.data)).catch(() => setUnits([]));
  }, []);

  const selectEtalon = (e) => { setSelected(e.id); setForm({ ...e }); setExpandedCerts(new Set()); };

  // Filtruj jednotky dle vybrané laboratoře (jednotka.quantity).
  const unitOptions = useMemo(() => {
    const filtered = form.laboratory
      ? units.filter(u => !u.quantity || u.quantity === form.laboratory)
      : units;
    return filtered.map(u => ({
      value: u.id,
      label: `${u.symbol} — ${u.description || u.code}`,
    }));
  }, [units, form.laboratory]);
  const unitById = useMemo(
    () => Object.fromEntries(units.map(u => [u.id, u])),
    [units]
  );

  const isDraft = selected === '__new__';
  const createDraft = () => {
    setSelected('__new__');
    setForm({ ...EMPTY_ETALON_DRAFT });
    setCalForm({ certificateNumber: '', issueDate: '', validUntil: '', status: 'active' });
    setExpandedCerts(new Set());
  };
  const cancelDraft = () => {
    setSelected(null);
    setForm({});
  };

  const missingRequired = ETALON_REQUIRED
    .filter(({ field }) => !String(form[field] || '').trim())
    .map(({ label }) => label);

  // Když uživatel přepne laboratoř, vyčistí se unitId pokud aktuální nepatří
  // do nové laboratoře (jinak by zůstalo neviditelné v dropdownu).
  const handleLaboratoryChange = (newLab) => {
    const current = unitById[form.unitId];
    if (current && current.quantity && current.quantity !== newLab) {
      setForm({ ...form, laboratory: newLab, unitId: '' });
    } else {
      setForm({ ...form, laboratory: newLab });
    }
  };

  const save = async () => {
    if (!selected) return;
    if (missingRequired.length > 0) {
      toast.error(`Povinná pole nejsou vyplněná: ${missingRequired.join(', ')}.`);
      return;
    }
    try {
      if (isDraft) {
        const res = await api.post('/etalons', form);
        load();
        selectEtalon(res.data);
        toast.success('Etalon vytvořen');
      } else {
        await api.put(`/etalons/${selected}`, form);
        load();
        toast.success('Uloženo');
      }
    } catch (err) {
      toastApiError(err);
    }
  };

  const remove = async () => {
    if (!selected || isDraft) return;
    if (!window.confirm('Smazat etalon?')) return;
    await api.delete(`/etalons/${selected}`);
    setSelected(null);
    load();
  };

  const calFormValid = calForm.certificateNumber.trim() && calForm.issueDate && calForm.validUntil;
  const addCalibration = () => {
    if (!calFormValid) {
      toast.error('Vyplňte číslo KL, datum vystavení a platnost.');
      return;
    }
    const cals = [...(form.calibrations || []), { ...calForm, attachmentPath: '', corrections: [] }];
    setForm({ ...form, calibrations: cals });
    setCalForm({ certificateNumber: '', issueDate: '', validUntil: '', status: 'active' });
  };

  const removeCalibration = async (idx) => {
    if (!window.confirm('Smazat tento kalibrační list etalonu?')) return;
    const cals = (form.calibrations || []).filter((_, i) => i !== idx);
    setForm({ ...form, calibrations: cals });
    setExpandedCerts(prev => {
      const next = new Set(prev);
      next.delete(idx);
      return next;
    });
    // U rozpracovaného etalonu jen lokálně (uloží se při vytvoření).
    if (isDraft || !selected) return;
    // Ulož okamžitě — nezávisle na povinných polích etalonu (jinak by se smazání
    // ztratilo, když save() blokuje kvůli nevyplněnému poli, a KL by se „vrátil").
    try {
      await api.put(`/etalons/${selected}`, { ...form, calibrations: cals });
      load();
      toast.success('Kalibrační list smazán');
    } catch (err) {
      toastApiError(err);
    }
  };

  const toggleCertExpanded = (idx) => {
    setExpandedCerts(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return next;
    });
  };

  // ─── Korekce per bod ─────────────────────────────────────────────────
  const addCorrection = (certIdx) => {
    const cals = [...(form.calibrations || [])];
    const corrections = [...(cals[certIdx].corrections || [])];
    corrections.push({ nominalValue: 0, correction: 0, uncertainty: 0 });
    cals[certIdx] = { ...cals[certIdx], corrections };
    setForm({ ...form, calibrations: cals });
  };
  const updateCorrection = (certIdx, corrIdx, field, value) => {
    const cals = [...(form.calibrations || [])];
    const corrections = [...(cals[certIdx].corrections || [])];
    corrections[corrIdx] = { ...corrections[corrIdx], [field]: value };
    cals[certIdx] = { ...cals[certIdx], corrections };
    setForm({ ...form, calibrations: cals });
  };
  const removeCorrection = (certIdx, corrIdx) => {
    const cals = [...(form.calibrations || [])];
    const corrections = (cals[certIdx].corrections || []).filter((_, i) => i !== corrIdx);
    cals[certIdx] = { ...cals[certIdx], corrections };
    setForm({ ...form, calibrations: cals });
  };

  // ─── PDF příloha ─────────────────────────────────────────────────────
  const triggerUpload = (certIdx) => {
    if (isDraft) {
      toast.error('Nejdřív etalon uložte, teprve pak můžete nahrát přílohu.');
      return;
    }
    setUploadingFor(certIdx);
    fileInputRef.current?.click();
  };

  const handleFileSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // reset, aby šlo nahrát stejný soubor znovu
    if (!file || uploadingFor === null) return;
    const certIdx = uploadingFor;
    setUploadingFor(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post(
        `/etalons/${selected}/calibrations/${certIdx}/attachment`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      const cals = [...(form.calibrations || [])];
      cals[certIdx] = { ...cals[certIdx], attachmentPath: res.data.attachmentPath };
      setForm({ ...form, calibrations: cals });
      toast.success('Příloha nahrána');
      load();
    } catch (err) {
      toastApiError(err, 'Nepodařilo se nahrát přílohu');
    }
  };

  const downloadAttachment = async (certIdx) => {
    if (isDraft) return;
    try {
      const token = localStorage.getItem('ct-token');
      const url = `${BACKEND_BASE}/api/etalons/${selected}/calibrations/${certIdx}/attachment`;
      const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) throw new Error('Stažení selhalo');
      const blob = await resp.blob();
      const dlUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = dlUrl;
      // Pokus o filename z Content-Disposition headeru, jinak generický
      const dispo = resp.headers.get('Content-Disposition') || '';
      const match = /filename="?([^"]+)"?/.exec(dispo);
      a.download = match ? match[1] : `etalon-${selected}-cert-${certIdx}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(dlUrl);
    } catch (err) {
      toast.error('Nepodařilo se stáhnout přílohu');
    }
  };

  const removeAttachment = async (certIdx) => {
    if (isDraft) return;
    if (!window.confirm('Smazat přílohu kalibračního listu?')) return;
    try {
      await api.delete(`/etalons/${selected}/calibrations/${certIdx}/attachment`);
      const cals = [...(form.calibrations || [])];
      cals[certIdx] = { ...cals[certIdx], attachmentPath: '' };
      setForm({ ...form, calibrations: cals });
      toast.success('Příloha smazána');
    } catch (err) {
      toastApiError(err, 'Nepodařilo se smazat přílohu');
    }
  };

  const isExpiring = (validUntil) => {
    if (!validUntil) return false;
    const d = new Date(validUntil);
    const now = new Date();
    const diff = (d - now) / (1000 * 60 * 60 * 24);
    return diff < 30;
  };

  const isExpired = (validUntil) => {
    if (!validUntil) return false;
    return new Date(validUntil) < new Date();
  };

  const getStatus = (e) => {
    for (const cal of (e.calibrations || [])) {
      if (isExpired(cal.validUntil)) return 'expired';
      if (isExpiring(cal.validUntil)) return 'expiring';
    }
    return 'ok';
  };

  const filtered = etalons.filter(e =>
    `${e.code} ${e.name} ${e.serialNumber}`.toLowerCase().includes(search.toLowerCase())
  );

  const sel = useBulkSelect(filtered.map(e => e.id));

  const bulkRemove = async () => {
    const ids = sel.selectedIds;
    const { ok, failed } = await bulkDelete(ids, id => api.delete(`/etalons/${id}`));
    if (selected && ids.includes(selected)) { setSelected(null); }
    sel.clear();
    load();
    if (failed) toast.warning(`Smazáno ${ok}, ${failed} selhalo (možná jsou v použití)`);
    else toast.success(`Smazáno ${ok}`);
  };

  const selectedUnit = unitById[form.unitId];

  return (
    <div data-testid="etalon-page" className="flex gap-4 h-[calc(100vh-7rem)]">
      {/* Hidden file input pro upload */}
      <input
        ref={fileInputRef}
        type="file"
        accept="application/pdf"
        onChange={handleFileSelected}
        className="hidden"
      />

      {/* Left: List */}
      <div className="ct-panel w-[420px] flex-shrink-0 flex flex-col">
        <div className="ct-panel-header flex items-center justify-between">
          <span>Etalony a zařízení</span>
          <button data-testid="add-etalon-btn" onClick={createDraft} className="p-1 rounded" style={{ color: 'var(--ct-accent)' }}>
            <Plus className="w-4 h-4" />
          </button>
        </div>
        <div className="p-2 space-y-2">
          <Input data-testid="etalon-search" placeholder="Hledat..." value={search} onChange={e => setSearch(e.target.value)} className="h-8 text-sm" />
          <BulkDeleteBar count={sel.count} onDelete={bulkRemove} onClear={sel.clear} entityLabel="etalonů" />
        </div>
        <ScrollArea className="flex-1">
          <table className="w-full text-sm">
            <thead>
              <tr style={{ borderBottom: '1px solid var(--ct-border)' }}>
                <th className="px-2 py-1.5 w-8">
                  <Checkbox checked={sel.allSelected} onCheckedChange={sel.toggleAll} aria-label="Vybrat vše" />
                </th>
                <th className="text-left px-3 py-1.5 text-xs" style={{ color: 'var(--ct-text-secondary)' }}>Označení</th>
                <th className="text-left px-3 py-1.5 text-xs" style={{ color: 'var(--ct-text-secondary)' }}>Název</th>
                <th className="text-left px-3 py-1.5 text-xs" style={{ color: 'var(--ct-text-secondary)' }}>Lab</th>
                <th className="text-left px-3 py-1.5 text-xs" style={{ color: 'var(--ct-text-secondary)' }}>Stav</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(e => {
                const status = getStatus(e);
                return (
                  <tr
                    key={e.id}
                    onClick={() => selectEtalon(e)}
                    className={`ct-table-row cursor-pointer ${selected === e.id ? 'active' : ''}`}
                  >
                    <td className="px-2 py-1.5 w-8" onClick={ev => ev.stopPropagation()}>
                      <Checkbox
                        checked={sel.isSelected(e.id)}
                        onCheckedChange={() => sel.toggle(e.id)}
                        aria-label={`Vybrat ${e.code}`}
                      />
                    </td>
                    <td className="px-3 py-1.5 font-mono">{e.code}</td>
                    <td className="px-3 py-1.5">{e.name}</td>
                    <td className="px-3 py-1.5 text-xs">{LAB_LABEL[e.laboratory] || '—'}</td>
                    <td className="px-3 py-1.5">
                      <StatusBadge status={status} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </ScrollArea>
      </div>

      {/* Middle: Detail */}
      {selected ? (
        <div className="flex-1 flex flex-col gap-4 overflow-y-auto min-h-0 pr-1 pb-6">
          <div className="ct-panel shrink-0">
            <div className="ct-panel-header flex items-center justify-between">
              <span>{isDraft ? 'Nový etalon (rozpracovaný)' : 'Detail etalonu'}</span>
              {!isDraft && (
                <button onClick={remove} className="p-1 rounded" style={{ color: 'var(--ct-error)' }}>
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
            {form._needsReview && !isDraft && (
              <div
                className="mx-4 mt-3 p-2 rounded text-xs flex items-center gap-2"
                style={{ background: 'var(--ct-warning-soft)', color: 'var(--ct-warning)' }}
              >
                <AlertTriangle className="w-4 h-4" />
                Migrace nebyla schopná spolehlivě určit laboratoř/jednotku. Prosím zkontrolujte hodnoty a uložte.
              </div>
            )}
            <div className="p-4 grid grid-cols-2 gap-3">
              <div className="ct-form-field">
                <label className="ct-form-label">Označení</label>
                <Input value={form.code || ''} onChange={e => setForm({ ...form, code: e.target.value })} className="h-9 text-sm font-mono" />
              </div>
              <div className="ct-form-field">
                <label className="ct-form-label">Název</label>
                <Input value={form.name || ''} onChange={e => setForm({ ...form, name: e.target.value })} className="h-9 text-sm" />
              </div>
              <div className="ct-form-field">
                <label className="ct-form-label">Výrobní číslo</label>
                <Input value={form.serialNumber || ''} onChange={e => setForm({ ...form, serialNumber: e.target.value })} className="h-9 text-sm font-mono" />
              </div>
              <div className="ct-form-field">
                <label className="ct-form-label">Laboratoř (veličina)</label>
                <select
                  value={form.laboratory || ''}
                  onChange={e => handleLaboratoryChange(e.target.value)}
                  className="w-full h-9 px-2 rounded border text-sm"
                  style={{ background: 'var(--ct-bg)', borderColor: 'var(--ct-border)', color: 'var(--ct-text)' }}
                >
                  <option value="">— vyberte —</option>
                  {LABORATORIES.map(l => (
                    <option key={l.code} value={l.code}>{l.label}</option>
                  ))}
                </select>
              </div>
              <div className="ct-form-field">
                <label className="ct-form-label">Jednotka</label>
                <select
                  value={form.unitId || ''}
                  onChange={e => setForm({ ...form, unitId: e.target.value })}
                  className="w-full h-9 px-2 rounded border text-sm"
                  style={{ background: 'var(--ct-bg)', borderColor: 'var(--ct-border)', color: 'var(--ct-text)' }}
                  disabled={!form.laboratory}
                  title={!form.laboratory ? 'Nejdřív vyberte laboratoř' : undefined}
                >
                  <option value="">{form.laboratory ? '— vyberte —' : '— nejdřív vyberte laboratoř —'}</option>
                  {unitOptions.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
              <div className="ct-form-field">
                <label className="ct-form-label">Rozsah od {selectedUnit ? `(${selectedUnit.symbol})` : ''}</label>
                <Input type="number" value={form.rangeFrom ?? ''} onChange={e => setForm({ ...form, rangeFrom: parseFloat(e.target.value) || null })} className="h-9 text-sm font-mono" />
              </div>
              <div className="ct-form-field">
                <label className="ct-form-label">Rozsah do {selectedUnit ? `(${selectedUnit.symbol})` : ''}</label>
                <Input type="number" value={form.rangeTo ?? ''} onChange={e => setForm({ ...form, rangeTo: parseFloat(e.target.value) || null })} className="h-9 text-sm font-mono" />
              </div>
              <div className="ct-form-field col-span-2">
                <label className="ct-form-label">Poznámka</label>
                <Textarea value={form.notes || ''} onChange={e => setForm({ ...form, notes: e.target.value })} className="text-sm" rows={2} />
              </div>
            </div>
            <div className="px-4 pb-4 flex items-center gap-2">
              <Button
                data-testid="save-etalon-btn"
                onClick={save}
                disabled={missingRequired.length > 0}
                className="ct-btn-primary gap-1"
                title={missingRequired.length > 0 ? `Chybí povinná pole: ${missingRequired.join(', ')}` : undefined}
              >
                <Save className="w-4 h-4" /> {isDraft ? 'Vytvořit' : 'Uložit'}
              </Button>
              {isDraft && (
                <Button onClick={cancelDraft} className="ct-btn-secondary">
                  Zrušit
                </Button>
              )}
              {missingRequired.length > 0 && (
                <span className="text-xs" style={{ color: 'var(--ct-text-disabled)' }}>
                  Chybí: {missingRequired.join(', ')}
                </span>
              )}
            </div>
          </div>

          {/* Calibration certificates — per-cert collapsible
              s sub-sekcí Korekce + Příloha */}
          <div className="ct-panel shrink-0">
            <div className="ct-panel-header">Kalibrační listy etalonu</div>
            <div className="p-4 space-y-2">
              {(form.calibrations || []).length === 0 && (
                <p className="text-xs" style={{ color: 'var(--ct-text-disabled)' }}>
                  Etalon zatím nemá žádné kalibrační listy. Přidejte první přes formulář dole.
                </p>
              )}
              {(form.calibrations || []).map((cal, idx) => {
                const isExpanded = expandedCerts.has(idx);
                const corrections = cal.corrections || [];
                const hasAttachment = !!cal.attachmentPath;
                return (
                  <div
                    key={idx}
                    className="rounded text-sm"
                    style={{
                      background: 'var(--ct-bg)',
                      border: `1px solid ${isExpired(cal.validUntil) ? 'var(--ct-error)' : isExpiring(cal.validUntil) ? 'var(--ct-warning)' : 'var(--ct-border)'}`,
                    }}
                  >
                    <div className="flex items-center gap-3 p-2">
                      <button
                        type="button"
                        onClick={() => toggleCertExpanded(idx)}
                        className="p-0.5"
                        style={{ color: 'var(--ct-text-secondary)' }}
                        title={isExpanded ? 'Sbalit' : 'Rozbalit'}
                      >
                        {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                      </button>
                      <span className="font-mono">{cal.certificateNumber}</span>
                      <span style={{ color: 'var(--ct-text-secondary)' }}>Platnost do: {cal.validUntil || '—'}</span>
                      {isExpired(cal.validUntil) && <AlertTriangle className="w-4 h-4" style={{ color: 'var(--ct-error)' }} />}
                      {isExpiring(cal.validUntil) && !isExpired(cal.validUntil) && <AlertTriangle className="w-4 h-4" style={{ color: 'var(--ct-warning)' }} />}
                      {hasAttachment && (
                        <Paperclip className="w-3.5 h-3.5" style={{ color: 'var(--ct-accent)' }} title="Příloha k dispozici" />
                      )}
                      {corrections.length > 0 && (
                        <span
                          className="text-[10px] font-bold rounded px-1.5 py-0.5"
                          style={{ background: 'var(--ct-accent)', color: '#fff' }}
                          title={`${corrections.length} korekcí`}
                        >
                          {corrections.length} korekcí
                        </span>
                      )}
                      <button
                        type="button"
                        onClick={() => removeCalibration(idx)}
                        title="Smazat tento KL"
                        className="ml-auto p-1 rounded"
                        style={{ color: 'var(--ct-error)' }}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    {isExpanded && (
                      <div className="px-3 pb-3 pt-1 space-y-3 border-t" style={{ borderColor: 'var(--ct-border)' }}>
                        {/* Příloha */}
                        <div>
                          <div className="flex items-center justify-between mb-1.5">
                            <span className="ct-eyebrow">Příloha kalibračního listu (PDF)</span>
                          </div>
                          {hasAttachment ? (
                            <div className="flex items-center gap-2">
                              <Paperclip className="w-3.5 h-3.5" style={{ color: 'var(--ct-accent)' }} />
                              <span className="text-xs font-mono" style={{ color: 'var(--ct-text-secondary)' }}>
                                {cal.attachmentPath.split('/').pop()}
                              </span>
                              <Button onClick={() => downloadAttachment(idx)} size="sm" className="ct-btn-secondary h-7 gap-1 text-xs">
                                <Download className="w-3 h-3" /> Stáhnout
                              </Button>
                              <Button onClick={() => triggerUpload(idx)} size="sm" className="ct-btn-secondary h-7 gap-1 text-xs">
                                <Upload className="w-3 h-3" /> Nahradit
                              </Button>
                              <Button onClick={() => removeAttachment(idx)} size="sm" className="ct-btn-secondary h-7 gap-1 text-xs" style={{ color: 'var(--ct-error)' }}>
                                <Trash2 className="w-3 h-3" /> Smazat
                              </Button>
                            </div>
                          ) : (
                            <Button
                              onClick={() => triggerUpload(idx)}
                              size="sm"
                              className="ct-btn-secondary h-7 gap-1 text-xs"
                              disabled={isDraft}
                              title={isDraft ? 'Nejdřív etalon uložte' : 'Nahrát PDF přílohu'}
                            >
                              <Upload className="w-3 h-3" /> Nahrát PDF
                            </Button>
                          )}
                        </div>

                        {/* Korekce per bod */}
                        <div>
                          <div className="flex items-center justify-between mb-1.5">
                            <span className="ct-eyebrow">Korekce v jednotlivých bodech</span>
                            <Button onClick={() => addCorrection(idx)} size="sm" className="ct-btn-secondary h-7 gap-1 text-xs">
                              <Plus className="w-3 h-3" /> Přidat řádek
                            </Button>
                          </div>
                          {corrections.length === 0 ? (
                            <p className="text-xs" style={{ color: 'var(--ct-text-disabled)' }}>
                              Žádné korekce. Přidejte řádek pro zápis nominální hodnoty, korekce a nejistoty.
                            </p>
                          ) : (
                            <table className="w-full text-xs">
                              <thead>
                                <tr style={{ color: 'var(--ct-text-secondary)' }}>
                                  <th className="text-left py-1 pr-2">Nominální hodnota {selectedUnit ? `(${selectedUnit.symbol})` : ''}</th>
                                  <th className="text-left py-1 pr-2">Korekce {selectedUnit ? `(${selectedUnit.symbol})` : ''}</th>
                                  <th className="text-left py-1 pr-2">Nejistota (k=2) {selectedUnit ? `(${selectedUnit.symbol})` : ''}</th>
                                  <th className="w-8"></th>
                                </tr>
                              </thead>
                              <tbody>
                                {corrections.map((c, cIdx) => (
                                  <tr key={cIdx} style={{ borderTop: '1px dashed var(--ct-border)' }}>
                                    <td className="py-1 pr-2">
                                      <Input
                                        type="number"
                                        step="any"
                                        value={c.nominalValue}
                                        onChange={e => updateCorrection(idx, cIdx, 'nominalValue', parseFloat(e.target.value) || 0)}
                                        className="h-7 text-xs font-mono"
                                      />
                                    </td>
                                    <td className="py-1 pr-2">
                                      <Input
                                        type="number"
                                        step="any"
                                        value={c.correction}
                                        onChange={e => updateCorrection(idx, cIdx, 'correction', parseFloat(e.target.value) || 0)}
                                        className="h-7 text-xs font-mono"
                                      />
                                    </td>
                                    <td className="py-1 pr-2">
                                      <Input
                                        type="number"
                                        step="any"
                                        min="0"
                                        value={c.uncertainty}
                                        onChange={e => updateCorrection(idx, cIdx, 'uncertainty', parseFloat(e.target.value) || 0)}
                                        className="h-7 text-xs font-mono"
                                      />
                                    </td>
                                    <td className="py-1">
                                      <button
                                        type="button"
                                        onClick={() => removeCorrection(idx, cIdx)}
                                        className="p-1 rounded"
                                        style={{ color: 'var(--ct-error)' }}
                                        title="Smazat řádek"
                                      >
                                        <Trash2 className="w-3 h-3" />
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                          <p className="text-[11px] mt-1" style={{ color: 'var(--ct-text-disabled)' }}>
                            Změny korekcí se uloží spolu s celým etalonem tlačítkem „Uložit" nahoře.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Form pro přidání nového KL */}
              <div className="grid grid-cols-4 gap-2 mt-3 pt-3 border-t" style={{ borderColor: 'var(--ct-border)' }}>
                <Input placeholder="Číslo KL" value={calForm.certificateNumber} onChange={e => setCalForm({ ...calForm, certificateNumber: e.target.value })} className="h-8 text-sm font-mono" />
                <Input type="date" value={calForm.issueDate} onChange={e => setCalForm({ ...calForm, issueDate: e.target.value })} className="h-8 text-sm" placeholder="Vystaveno" title="Datum vystavení" />
                <Input type="date" value={calForm.validUntil} onChange={e => setCalForm({ ...calForm, validUntil: e.target.value })} className="h-8 text-sm" placeholder="Platnost do" title="Platnost do" />
                <Button
                  onClick={addCalibration}
                  disabled={!calFormValid}
                  size="sm"
                  className="ct-btn-primary h-8"
                  title={!calFormValid ? 'Vyplňte číslo KL, datum vystavení a platnost.' : undefined}
                >
                  <Plus className="w-3 h-3 mr-1" /> Přidat KL
                </Button>
              </div>
            </div>
          </div>

        </div>
      ) : (
        <div className="flex-1 flex items-center justify-center">
          <EmptyState
            icon={Ruler}
            title="Vyberte etalon"
            description="Klikněte na položku v seznamu vlevo pro zobrazení detailu a historie kalibrací."
          />
        </div>
      )}
    </div>
  );
}
